import { Box, Icon, Text } from '@bechtel/gds';
import { useAppUser } from '@bechtel/ht-clients-common';
import {
  Requisition,
  RequisitionCatalogComment,
  RequisitionCatalogToolRoomInput,
  RequisitionToolCatalogInput,
  ToolCatalog,
} from '@bechtel/ht-types';
import { filter, findIndex, map, sumBy } from 'lodash';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useToolRoomContext } from '~/context/tool-room-context';
import { CatalogItem } from './catalog-item';
import { PreviousRevisions } from './previous-revisions';
import {
  CommentContainerStyled,
  CommentIconStyled,
  InputFieldStyled,
  StickyColumnStyled,
  ToolRoomTdStyled,
} from './styled';

interface ItemRowProps {
  toolCatalog: ToolCatalog;
  updateItemsToSave: (val: RequisitionToolCatalogInput) => void;
  itemsToSaveCount: number;
  requisition: Requisition;
  onCommentClick: (
    toolRoomId: string,
    comments: RequisitionCatalogComment[],
    type: string,
    stats: {
      QOH: number;
      QOR: number;
      QN: number;
      qtyReq: number;
    },
    isLocked: boolean
  ) => void;
}

export interface ExtendedRequisitionCatalogToolRoomInput extends RequisitionCatalogToolRoomInput {
  requisitionCatalogComments?: RequisitionCatalogComment[];
}
const ItemRow: FC<ItemRowProps> = ({
  toolCatalog,
  updateItemsToSave,
  itemsToSaveCount,
  requisition,
  onCommentClick,
}) => {
  const { appUser } = useAppUser();
  const theme = useTheme();
  const [itemsValues, setItemsValues] = useState<ExtendedRequisitionCatalogToolRoomInput[]>([]);
  const { showExtendedStats } = useToolRoomContext();
  const { t } = useTranslation();
  const initialReqCatalogToolRoomValues = map(appUser?.toolRooms, (toolRoom) => {
    const updatedReqCatalogTRItem = toolCatalog?.requisitionToolCatalog?.requisitionCatalogToolRooms.find((item) => {
      return item?.toolRoomId === toolRoom?.id;
    });
    return {
      toolRoomId: toolRoom?.id || '',
      quantityOrdered: updatedReqCatalogTRItem?.quantityOrdered ?? 0,
      id: updatedReqCatalogTRItem?.id ?? '',
      requisitionCatalogComments:
        (updatedReqCatalogTRItem &&
          (updatedReqCatalogTRItem.requisitionCatalogComments as RequisitionCatalogComment[])) ||
        [],
    };
  });

  const [hoveredToolRoomId, setHoveredToolRoomId] = useState<string | null>(null); // Track hovered tool room

  const [requisitionCatalogToolRooms, setRequisitionCatalogToolRooms] = useState<
    ExtendedRequisitionCatalogToolRoomInput[]
  >([]);
  useEffect(() => {
    setRequisitionCatalogToolRooms(initialReqCatalogToolRoomValues);
  }, [toolCatalog]);
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const totalRequestedQty = sumBy(toolCatalog?.requisitionToolCatalog?.requisitionCatalogToolRooms, 'quantityOrdered');

  const onValueChange = (toolRoomId: string, quantityOrdered: number, id?: string) => {
    const existingItemIndex = findIndex(itemsValues, (item) => item?.toolRoomId === toolRoomId);
    if (existingItemIndex > -1) {
      const updatedArray = [...itemsValues]; // Create a copy of the array
      updatedArray[existingItemIndex] = { ...updatedArray[existingItemIndex], quantityOrdered, id }; // Update the object at the given index
      setItemsValues(updatedArray);
    } else {
      setItemsValues((prevState) => {
        return [...prevState, { toolRoomId, quantityOrdered, id }];
      });
    }
  };

  const isToolRoomLocked = (toolRoom) => {
    const lockedToolRooms = filter(requisition?.requisitionToolRoomLocks, (item) => {
      return toolRoom.id == item?.toolRoomId;
    });
    return lockedToolRooms?.length > 0;
  };

  const trQtyOnHand = useCallback(
    (toolRoomId: string) => {
      const tr = toolCatalog.toolRoomQuantityOnHand?.find((item) => item?.toolRoomId === toolRoomId);
      if (tr) {
        return tr.quantityOnHand;
      } else return 0;
    },
    [toolCatalog.toolRoomQuantityOnHand]
  );

  const trQtyOnOrder = useCallback(
    (toolRoomId: string) => {
      const tr = toolCatalog.toolRoomsQuantityOnOrder?.find((item) => item?.toolRoomId === toolRoomId);
      if (tr) {
        return tr.quantityOnOrder;
      } else return 0;
    },
    [toolCatalog.toolRoomsQuantityOnOrder]
  );

  const handleTdFocus = (index: number, toolRoomId?: string) => {
    setTimeout(() => {
      inputRefs.current.get(`td-${index}-${toolRoomId}`)?.focus();
    }, 0);
  };

  useEffect(() => {
    if (!itemsValues.length) return;
    updateItemsToSave({
      id: toolCatalog?.requisitionToolCatalog?.id,
      toolCatalogId: toolCatalog.id,
      quantityOnHand: toolCatalog.quantityOnHand || 0,
      quantityOrdered: toolCatalog.quantityOnOrder,
      requisitionCatalogToolRooms: itemsValues,
    });
  }, [itemsValues]);

  useEffect(() => {
    if (!itemsToSaveCount) {
      setItemsValues([]);
    }
  }, [itemsToSaveCount]);

  const handleCommentClick = (index, toolRoom) => {
    const toolRoomId =
      requisitionCatalogToolRooms[index].id == '' ? toolRoom?.id : requisitionCatalogToolRooms[index].id;
    const comments = requisitionCatalogToolRooms[index].requisitionCatalogComments;
    const qtyOnHand = trQtyOnHand(toolRoom?.id ?? '');
    const qtyOnOrder = trQtyOnOrder(toolRoom?.id ?? '');
    const qtyRequired = qtyOnOrder - qtyOnHand;
    const qtyOrdered = requisitionCatalogToolRooms[index]?.quantityOrdered ?? '';
    onCommentClick(
      toolRoomId,
      comments || [],
      requisitionCatalogToolRooms[index].id == '' ? 'default' : 'add',
      {
        QOH: qtyOnHand,
        QOR: qtyOnOrder,
        QN: qtyRequired,
        qtyReq: qtyOrdered,
      },
      isToolRoomLocked(toolRoom)
    );
  };
  return (
    <tr>
      <StickyColumnStyled style={{ width: '340px', left: 0 }}>
        <CatalogItem
          catalogName={toolCatalog?.name || ''}
          uom={toolCatalog.uom}
          bscnPartNumber={toolCatalog.bscnPartNumber}
          toolCatalogImageUrl={toolCatalog.toolCatalogImageUrl}
          toolCatalogThumbnailImageUrl={toolCatalog.toolCatalogThumbnailImageUrl}
          fallbackName={toolCatalog.toolCategory?.name}
        />
      </StickyColumnStyled>
      <StickyColumnStyled style={{ background: theme.colors.grey[50], left: '340px' }}>
        {toolCatalog.quantityOnHand}
      </StickyColumnStyled>
      <StickyColumnStyled style={{ background: theme.colors.grey[50], left: '440px' }}>
        {toolCatalog.quantityOnOrder}
      </StickyColumnStyled>
      <StickyColumnStyled style={{ background: theme.colors.grey[50], left: '540px' }}>
        {Number(toolCatalog?.quantityOnOrder) - Number(toolCatalog?.quantityOnHand) > 0
          ? Number(toolCatalog.quantityOnOrder) - Number(toolCatalog.quantityOnHand)
          : 0}
      </StickyColumnStyled>

      {appUser?.toolRooms &&
        appUser.toolRooms.length > 0 &&
        map(appUser?.toolRooms, (toolRoom, index) => {
          const updatedReqCatalogTRItem = toolCatalog?.requisitionToolCatalog?.requisitionCatalogToolRooms.find(
            (item) => item?.toolRoomId === toolRoom?.id
          );
          return (
            <ToolRoomTdStyled
              key={toolRoom?.id + toolCatalog?.id}
              isUpdated={Boolean(itemsValues.find((item) => item?.toolRoomId === toolRoom?.id))}
              isLocked={isToolRoomLocked(toolRoom)}
              onClick={() => handleTdFocus(index, toolRoom?.id)}
              onMouseEnter={() => {
                setHoveredToolRoomId(toolRoom?.id ?? '');
              }} // Set hovered tool room ID on mouse enter
              onMouseLeave={() => setHoveredToolRoomId(null)}
            >
              <CommentContainerStyled>
                <CommentIconStyled
                  onClick={() => {
                    handleCommentClick(index, toolRoom);
                  }}
                >
                  {(requisitionCatalogToolRooms[index]?.requisitionCatalogComments?.length ?? 0) > 0 ? (
                    <Icon icon="comment-lines" iconStyle="solid" fillColor="primary.700" />
                  ) : hoveredToolRoomId === toolRoom?.id && !isToolRoomLocked(toolRoom) ? (
                    <Icon icon="comment-lines" iconStyle="regular" fillColor="primary.700" />
                  ) : null}
                </CommentIconStyled>
              </CommentContainerStyled>
              <Box justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
                <Box flexDirection={{ sm: 'column' }}>
                  <Box justifyContent={{ sm: 'space-between' }} css={{ letterSpacing: '1px' }}>
                    <InputFieldStyled
                      ref={(el) => {
                        if (el) inputRefs.current.set(`td-${index}-${toolRoom?.id}`, el);
                      }}
                      placeholder="0"
                      type="text"
                      disabled={isToolRoomLocked(toolRoom)}
                      value={requisitionCatalogToolRooms[index]?.quantityOrdered ?? ''}
                      style={{ width: '80%' }}
                      onChange={(e) => {
                        if (isNaN(Number(e.target.value))) return;
                        setRequisitionCatalogToolRooms((prevState) => {
                          const updatedArray = [...prevState]; // Create a copy of the array
                          updatedArray[index] = {
                            ...updatedArray[index],
                            quantityOrdered: Number(e.target.value) ?? 0,
                          }; // Update the object at the given index
                          return updatedArray;
                        });
                        onValueChange(toolRoom?.id || '', Number(e.target.value), updatedReqCatalogTRItem?.id);
                      }}
                    />
                  </Box>

                  {showExtendedStats && (
                    <Box justifyContent={{ sm: 'space-between' }} css={{ letterSpacing: '1px' }}>
                      {(trQtyOnOrder(toolRoom?.id ?? '') > 0 || trQtyOnHand(toolRoom?.id ?? '') > 0) && (
                        <Box alignItems={{ sm: 'center' }}>
                          <Text size={300} weight={600} family="openSansCondensed" color="grey.700">
                            {t('QOH')}:
                          </Text>
                          <Text color="grey.700" size={300} weight={600}>
                            {trQtyOnHand(toolRoom?.id ?? '')}
                          </Text>
                        </Box>
                      )}
                      {trQtyOnOrder(toolRoom?.id ?? '') > 0 && (
                        <Box alignItems={{ sm: 'center' }}>
                          <Text size={300} weight={600} family="openSansCondensed" color="grey.700">
                            {t('QOR')}:
                          </Text>
                          <Text color="grey.700" size={300} weight={600}>
                            {trQtyOnOrder(toolRoom?.id ?? '')}
                          </Text>
                        </Box>
                      )}
                      {trQtyOnOrder(toolRoom?.id ?? '') - trQtyOnHand(toolRoom?.id ?? '') > 0 && (
                        <Box alignItems={{ sm: 'center' }}>
                          <Text size={300} weight={600} family="openSansCondensed" color="grey.700">
                            {t('QN')}:
                          </Text>
                          <Text color="grey.700" size={300} weight={600}>
                            {trQtyOnOrder(toolRoom?.id ?? '') - trQtyOnHand(toolRoom?.id ?? '')}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </ToolRoomTdStyled>
          );
        })}
      <td style={{ background: theme.colors.grey[50] }}>{totalRequestedQty || 0}</td>
      <td>
        {toolCatalog.previousRequisitionToolCatalog ? (
          <PreviousRevisions previousRevisions={toolCatalog.previousRequisitionToolCatalog} />
        ) : (
          '-'
        )}
      </td>
    </tr>
  );
};

export default ItemRow;
